import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  Home, ArrowLeft, CheckCircle, XCircle, RotateCcw,
  Trophy, Brain, ListChecks, ArrowRight, BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VocabularyQuiz() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [quizType, setQuizType] = useState(null); // null = selection, 'vocabulary' or 'identification'
  const [quiz, setQuiz] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchQuiz = async (type) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quiz/${type}/${unitId}?count=10`);
      setQuiz(res.data);
      setQuizType(type);
      setCurrentQ(0);
      setSelected(null);
      setAnswered(false);
      setScore(0);
      setFinished(false);
      setAnswers([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (option) => {
    if (answered) return;
    setSelected(option);
  };

  const handleSubmitAnswer = () => {
    if (selected === null) return;
    const question = quiz.questions[currentQ];
    const isCorrect = selected === question.correct_answer;
    if (isCorrect) setScore(prev => prev + 1);
    setAnswered(true);
    setAnswers(prev => [...prev, { term: question.term, selected, correct: question.correct_answer, isCorrect }]);
  };

  const handleNext = () => {
    if (currentQ + 1 >= quiz.questions.length) {
      setFinished(true);
      recordScore();
    } else {
      setCurrentQ(prev => prev + 1);
      setSelected(null);
      setAnswered(false);
    }
  };

  const recordScore = async () => {
    if (!user || !token) return;
    try {
      await axios.post(`${API}/progress/quiz/${user.id}/${unitId}`, {
        quiz_type: quizType,
        score: score + (selected === quiz.questions[currentQ]?.correct_answer ? 1 : 0),
        max_score: quiz.questions.length
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (_err) { /* Best-effort quiz score save; failure is non-blocking */ }
  };

  const resetQuiz = () => {
    setQuizType(null);
    setQuiz(null);
    setFinished(false);
  };

  // Quiz type selection screen
  if (!quizType) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/unit/${unitId}`)}
            className="mb-6 text-[#5A6577] hover:text-[#14213D]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />Back to Unit
          </Button>
          <div className="text-center mb-8">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Study Quiz</p>
            <h2 className="font-serif-display text-3xl text-[#14213D]">Choose Your Quiz</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6 cursor-pointer hover:shadow-lg hover:border-[#14213D]/20 transition-all group border-[#EBE7E0]" onClick={() => fetchQuiz('vocabulary')}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#F9F8F4] border border-[#EBE7E0] flex items-center justify-center mx-auto mb-4 group-hover:bg-[#FBF6E8] transition-colors">
                  <BookOpen className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif-display text-lg text-[#14213D] mb-2">Vocabulary Quiz</h3>
                <p className="text-sm text-[#5A6577]">Match terms with their definitions</p>
              </div>
            </Card>
            <Card className="p-6 cursor-pointer hover:shadow-lg hover:border-[#14213D]/20 transition-all group border-[#EBE7E0]" onClick={() => fetchQuiz('identification')}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#F9F8F4] border border-[#EBE7E0] flex items-center justify-center mx-auto mb-4 group-hover:bg-[#EBE7E0] transition-colors">
                  <ListChecks className="h-8 w-8 text-[#14213D]" />
                </div>
                <h3 className="font-serif-display text-lg text-[#14213D] mb-2">Identification Quiz</h3>
                <p className="text-sm text-[#5A6577]">Identify people, events, and themes</p>
              </div>
            </Card>
          </div>
          {loading && (
            <div className="flex justify-center mt-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent" />
            </div>
          )}
        </div>
      </AppShell>
    );
  }

  if (loading || !quiz) {
    return <AppShell><div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent" /></div></AppShell>;
  }

  // Results screen
  if (finished) {
    const finalScore = answers.filter(a => a.isCorrect).length;
    const pct = Math.round((finalScore / quiz.questions.length) * 100);
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/unit/${unitId}`)}
            className="mb-6 text-[#5A6577] hover:text-[#14213D]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />Back to Unit
          </Button>
          <Card className="p-8 text-center mb-8 border-[#EBE7E0]">
            <Trophy className={`h-16 w-16 mx-auto mb-4 ${pct >= 80 ? 'text-[#D4AF37]' : pct >= 60 ? 'text-[#14213D]' : 'text-[#A0AEC0]'}`} />
            <h2 className="font-serif-display text-3xl text-[#14213D] mb-2">{finalScore} / {quiz.questions.length}</h2>
            <p className="text-lg text-[#5A6577] mb-4">{pct}% Correct</p>
            <Progress value={pct} className="h-3 max-w-xs mx-auto mb-4" />
            <p className="text-sm text-[#5A6577]">
              {pct >= 90 ? 'Excellent work!' : pct >= 70 ? 'Good job! Keep studying.' : 'Keep practicing — you\'ll improve!'}
            </p>
          </Card>

          {/* Answer Review */}
          <h3 className="font-serif-display text-lg text-[#14213D] mb-4">Review Your Answers</h3>
          <div className="space-y-3">
            {answers.map((a, i) => (
              <Card key={`ans-${i}-${a.term?.slice(0,20)}`} className={`p-4 border-l-4 ${a.isCorrect ? 'border-l-green-500' : 'border-l-red-500'}`}>
                <div className="flex items-start gap-3">
                  {a.isCorrect ? <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" /> : <XCircle className="h-5 w-5 text-red-600 mt-0.5" />}
                  <div className="flex-1">
                    <h4 className="font-medium text-[#14213D]">{a.term}</h4>
                    {!a.isCorrect && (
                      <>
                        <p className="text-sm text-red-600 line-through mt-1">{a.selected?.substring(0, 120)}...</p>
                        <p className="text-sm text-green-700 mt-1">{a.correct?.substring(0, 120)}...</p>
                      </>
                    )}
                    {a.isCorrect && <p className="text-sm text-green-700 mt-1">{a.correct?.substring(0, 120)}...</p>}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="flex gap-3 mt-8">
            <Button variant="outline" className="flex-1" onClick={resetQuiz}>
              <RotateCcw className="h-4 w-4 mr-2" />Try Different Quiz
            </Button>
            <Button className="flex-1 btn-primary" onClick={() => fetchQuiz(quizType)}>
              <RotateCcw className="h-4 w-4 mr-2" />Retake Quiz
            </Button>
          </div>
        </div>
      </AppShell>
    );
  }

  // Active quiz screen
  const question = quiz.questions[currentQ];
  const progress = ((currentQ + (answered ? 1 : 0)) / quiz.questions.length) * 100;

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { if (window.confirm('Leave quiz? Progress will be lost.')) resetQuiz(); }}
            className="text-[#5A6577] hover:text-[#14213D]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />Exit
          </Button>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-[#5A6577]">{currentQ + 1} / {quiz.total_questions}</span>
            <span className="font-medium text-[#14213D]">Score: {score}</span>
          </div>
        </div>
        <h1 className="font-serif-display text-xl text-[#14213D] mb-4">{quiz.unit_title}</h1>
        <Progress value={progress} className="h-1.5 mb-8" />

        <Card className="p-6 mb-6 border-[#EBE7E0]">
          <div className="flex items-center gap-2 mb-4">
            <span className={`text-[11px] px-2 py-1 rounded font-semibold uppercase tracking-wider ${quizType === 'vocabulary' ? 'bg-[#FBF6E8] text-[#14213D]' : 'bg-[#F9F8F4] text-[#14213D]'}`}>
              {quizType === 'vocabulary' ? 'Vocabulary' : question.type || 'Identification'}
            </span>
            <span className="text-xs text-[#5A6577]">Question {currentQ + 1}</span>
          </div>
          <h2 className="font-serif-display text-xl text-[#14213D] mb-2">{question.term}</h2>
          {question.context && <p className="text-sm text-[#5A6577] mb-4 italic">{question.context}</p>}
          <p className="text-sm text-[#5A6577]">Select the correct {quizType === 'vocabulary' ? 'definition' : 'description'}:</p>
        </Card>

        <div className="space-y-3 mb-6">
          {question.options.map((option, i) => {
            let borderClass = 'border-[#EBE7E0] hover:border-[#14213D]/30';
            let bgClass = 'bg-white';

            if (answered) {
              if (option === question.correct_answer) {
                borderClass = 'border-green-500';
                bgClass = 'bg-green-50';
              } else if (option === selected && option !== question.correct_answer) {
                borderClass = 'border-red-500';
                bgClass = 'bg-red-50';
              }
            } else if (option === selected) {
              borderClass = 'border-[#14213D]';
              bgClass = 'bg-slate-50';
            }

            return (
              <Card
                key={`opt-${i}-${option.slice(0,30)}`}
                className={`p-4 cursor-pointer border-2 transition-all ${borderClass} ${bgClass}`}
                onClick={() => handleSelect(option)}
              >
                <div className="flex items-start gap-3">
                  <span className="text-sm font-bold text-[#A0AEC0] mt-0.5">{String.fromCharCode(65 + i)}.</span>
                  <p className="text-sm text-[#14213D] flex-1">{option}</p>
                  {answered && option === question.correct_answer && <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />}
                  {answered && option === selected && option !== question.correct_answer && <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />}
                </div>
              </Card>
            );
          })}
        </div>

        {answered && question.significance && (
          <Card className="p-4 bg-blue-50 border-blue-200 mb-6">
            <p className="text-sm text-blue-900"><strong>Key Insight:</strong> {question.significance}</p>
          </Card>
        )}

        <div className="flex justify-end">
          {!answered ? (
            <Button onClick={handleSubmitAnswer} disabled={selected === null} className="btn-primary">
              Check Answer
            </Button>
          ) : (
            <Button onClick={handleNext} className="btn-primary">
              {currentQ + 1 >= quiz.questions.length ? 'See Results' : 'Next Question'} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </AppShell>
  );
}
